// File: src/utils/core/errors.ts

/**
 * DysTest Clinical Error System
 * Domain: Core (Pure error classes for clinical applications)
 *
 * Features:
 * - Pure error classes with zero dependencies
 * - Clinical-specific error types
 * - Import types from core.types.ts
 * - HIPAA-compliant error sanitization
 *
 * @author DysTest Clinical Team
 * @version 3.0.0 - Atomic Architecture
 */

// Import from the unified type system
import type { ClinicalErrorDetails } from '@/types';

// ============================================================================
// BASE ERROR CLASS
// ============================================================================

export class DystestError extends Error {
  public readonly code: string;
  public readonly timestamp: Date;
  public readonly context?: Record<string, any> | undefined;
  public readonly isOperational: boolean;
  public readonly severity: "low" | "medium" | "high";
  public readonly recoveryAction?: string | undefined;

  constructor(
    message: string,
    code: string = "DYSTEST_ERROR",
    context?: Record<string, any> | undefined,
    isOperational: boolean = true,
    severity: "low" | "medium" | "high" = "medium",
    recoveryAction?: string | undefined
  ) {
    super(message);
    this.name = "DystestError";
    this.code = code;
    this.timestamp = new Date();
    this.context = context;
    this.isOperational = isOperational;
    this.severity = severity;
    this.recoveryAction = recoveryAction;

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Get error details (implements ClinicalErrorDetails interface)
   */
  getDetails(): ClinicalErrorDetails {
    // Build the details object directly with conditional properties
    const details: Omit<ClinicalErrorDetails, 'context' | 'recoveryAction'> = {
      code: this.code,
      message: this.message,
      timestamp: this.timestamp.toISOString(),
      severity: this.severity,
    };

    // Create the full object with optional properties
    const fullDetails: ClinicalErrorDetails = {
      ...details,
      ...(this.context !== undefined && { context: this.sanitizeContext(this.context) }),
      ...(this.recoveryAction !== undefined && { recoveryAction: this.recoveryAction }),
    };

    return fullDetails;
  }

  /**
   * Sanitize context to remove PII (HIPAA compliance)
   */
  private sanitizeContext(
    context: Record<string, any>
  ): Record<string, any> {
    const piiFields = [
      "email",
      "phone",
      "ssn",
      "address",
      "name",
      "birthDate",
      "ip",
      "creditCard",
      "password",
      "token",
      "session",
      "patientId",
      "medicalRecordNumber",
      "insuranceId",
    ];

    const sanitized = { ...context };

    for (const field of piiFields) {
      if (sanitized[field]) {
        sanitized[field] = "[REDACTED]";
      }
    }

    return sanitized;
  }

  /**
   * Convert to JSON for serialization
   */
  toJSON(): ClinicalErrorDetails {
    return this.getDetails();
  }

  /**
   * Create a user-friendly error message
   */
  toUserMessage(): string {
    const baseMessage = this.message;
    if (this.recoveryAction !== undefined) {
      return `${baseMessage}. ${this.recoveryAction}`;
    }
    return baseMessage;
  }
}

// ============================================================================
// CLINICAL ASSESSMENT ERRORS
// ============================================================================

export class ClinicalAssessmentError extends DystestError {
  constructor(
    message: string,
    code: string = "CLINICAL_ASSESSMENT_ERROR",
    context?: Record<string, any> | undefined,
    severity: "low" | "medium" | "high" = "medium",
    recoveryAction?: string | undefined
  ) {
    super(message, code, context, true, severity, recoveryAction);
    this.name = "ClinicalAssessmentError";
  }
}

export class DyslexiaAnalysisError extends ClinicalAssessmentError {
  constructor(
    message: string = "Dyslexia analysis error",
    context?: Record<string, any> | undefined,
    severity: "low" | "medium" | "high" = "medium"
  ) {
    super(message, "DYSL_EXIA_ANALYSIS_ERROR", context, severity);
    this.name = "DyslexiaAnalysisError";
  }
}

// ============================================================================
// VISION & GAZE ERRORS
// ============================================================================

export class VisionProcessingError extends DystestError {
  constructor(
    message: string = "Vision processing error",
    context?: Record<string, any> | undefined,
    code: string = "VISION_PROCESSING_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "VisionProcessingError";
  }
}

export class GazeTrackingError extends DystestError {
  constructor(
    message: string = "Gaze tracking error",
    context?: Record<string, any> | undefined,
    code: string = "GAZE_TRACKING_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "GazeTrackingError";
  }
}

export class CalibrationError extends DystestError {
  constructor(
    message: string = "Calibration error",
    context?: Record<string, any> | undefined,
    code: string = "CALIBRATION_ERROR"
  ) {
    super(message, code, context, true, "high");
    this.name = "CalibrationError";
  }
}

// ============================================================================
// KEYSTROKE ERRORS
// ============================================================================

export class KeystrokeAnalysisError extends DystestError {
  constructor(
    message: string = "Keystroke analysis error",
    context?: Record<string, any> | undefined,
    code: string = "KEYSTROKE_ANALYSIS_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "KeystrokeAnalysisError";
  }
}

export class DictionaryError extends DystestError {
  constructor(
    message: string = "Clinical dictionary error",
    context?: Record<string, any> | undefined,
    code: string = "DICTIONARY_ERROR"
  ) {
    super(message, code, context, true, "high");
    this.name = "DictionaryError";
  }
}

// ============================================================================
// PERFORMANCE & HARDWARE ERRORS
// ============================================================================

export class PerformanceError extends DystestError {
  constructor(
    message: string = "Performance error",
    context?: Record<string, any> | undefined,
    code: string = "PERFORMANCE_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "PerformanceError";
  }
}

export class CameraError extends DystestError {
  constructor(
    message: string = "Camera error",
    context?: Record<string, any> | undefined,
    code: string = "CAMERA_ERROR"
  ) {
    super(message, code, context, true, "high");
    this.name = "CameraError";
  }
}

export class HardwareError extends DystestError {
  constructor(
    message: string = "Hardware error",
    context?: Record<string, any> | undefined,
    code: string = "HARDWARE_ERROR"
  ) {
    super(message, code, context, true, "high");
    this.name = "HardwareError";
  }
}

// ============================================================================
// VALIDATION & DATA ERRORS
// ============================================================================

export class ValidationError extends DystestError {
  constructor(
    message: string = "Validation error",
    context?: Record<string, any> | undefined,
    code: string = "VALIDATION_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "ValidationError";
  }
}

export class DataIntegrityError extends DystestError {
  constructor(
    message: string = "Data integrity error",
    context?: Record<string, any> | undefined,
    code: string = "DATA_INTEGRITY_ERROR"
  ) {
    super(message, code, context, true, "high");
    this.name = "DataIntegrityError";
  }
}

// ============================================================================
// CONFIGURATION ERRORS
// ============================================================================

export class ConfigurationError extends DystestError {
  constructor(
    message: string = "Configuration error",
    context?: Record<string, any> | undefined,
    code: string = "CONFIGURATION_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "ConfigurationError";
  }
}

export class SettingsError extends DystestError {
  constructor(
    message: string = "Settings error",
    context?: Record<string, any> | undefined,
    code: string = "SETTINGS_ERROR"
  ) {
    super(message, code, context, true, "low");
    this.name = "SettingsError";
  }
}

// ============================================================================
// UI & USER INTERACTION ERRORS
// ============================================================================

export class UIError extends DystestError {
  constructor(
    message: string = "UI error",
    context?: Record<string, any> | undefined,
    code: string = "UI_ERROR"
  ) {
    super(message, code, context, true, "low");
    this.name = "UIError";
  }
}

export class UserInputError extends DystestError {
  constructor(
    message: string = "User input error",
    context?: Record<string, any> | undefined,
    code: string = "USER_INPUT_ERROR"
  ) {
    super(message, code, context, true, "low");
    this.name = "UserInputError";
  }
}

// ============================================================================
// NETWORK & API ERRORS
// ============================================================================

export class NetworkError extends DystestError {
  constructor(
    message: string = "Network error",
    context?: Record<string, any> | undefined,
    code: string = "NETWORK_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "NetworkError";
  }
}

export class APIError extends DystestError {
  constructor(
    message: string = "API error",
    context?: Record<string, any> | undefined,
    code: string = "API_ERROR"
  ) {
    super(message, code, context, true, "medium");
    this.name = "APIError";
  }
}

// ============================================================================
// ERROR UTILITIES (PURE FUNCTIONS)
// ============================================================================

/**
 * Check if error is operational (expected vs unexpected)
 */
export function isOperationalError(error: Error): boolean {
  return error instanceof DystestError && error.isOperational;
}

/**
 * Check if error is clinical/healthcare related
 */
export function isClinicalError(error: Error): boolean {
  return error instanceof ClinicalAssessmentError;
}

/**
 * Create error from unknown type (TypeScript safe)
 */
export function createError(
  error: unknown,
  defaultMessage: string = "An unexpected error occurred"
): DystestError {
  if (error instanceof DystestError) {
    return error;
  }

  if (error instanceof Error) {
    return new DystestError(
      error.message || defaultMessage,
      "UNKNOWN_ERROR",
      { originalError: error.name } as Record<string, any> | undefined,
      false,
      "medium"
    );
  }

  return new DystestError(
    defaultMessage,
    "UNKNOWN_ERROR",
    { originalValue: error } as Record<string, any> | undefined,
    false,
    "medium"
  );
}

/**
 * Wrap async function with error handling
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  errorHandler?: (error: DystestError) => void
): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    const dystestError = createError(error);
    
    if (errorHandler) {
      errorHandler(dystestError);
    } else {
      console.error("Unhandled error:", dystestError.getDetails());
    }
    
    return null;
  }
}

/**
 * Error severity comparator
 */
export function compareSeverity(
  a: "low" | "medium" | "high",
  b: "low" | "medium" | "high"
): number {
  const severityMap = { low: 0, medium: 1, high: 2 };
  return severityMap[a] - severityMap[b];
}

/**
 * Create a sanitized error for logging (HIPAA compliant)
 */
export function createSanitizedError(
  error: DystestError
): ClinicalErrorDetails {
  return error.getDetails();
}

/**
 * Check if error should be shown to user
 */
export function isUserFacingError(error: DystestError): boolean {
  return error.severity !== "low" && error.isOperational;
}

// ============================================================================
// ERROR CODE CONSTANTS
// ============================================================================

export const ERROR_CODES = {
  // Core errors
  DYSTEST_ERROR: "DYSTEST_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
  
  // Clinical errors
  CLINICAL_ASSESSMENT_ERROR: "CLINICAL_ASSESSMENT_ERROR",
  DYSL_EXIA_ANALYSIS_ERROR: "DYSL_EXIA_ANALYSIS_ERROR",
  
  // Vision errors
  VISION_PROCESSING_ERROR: "VISION_PROCESSING_ERROR",
  GAZE_TRACKING_ERROR: "GAZE_TRACKING_ERROR",
  CALIBRATION_ERROR: "CALIBRATION_ERROR",
  
  // Keystroke errors
  KEYSTROKE_ANALYSIS_ERROR: "KEYSTROKE_ANALYSIS_ERROR",
  DICTIONARY_ERROR: "DICTIONARY_ERROR",
  
  // Performance errors
  PERFORMANCE_ERROR: "PERFORMANCE_ERROR",
  CAMERA_ERROR: "CAMERA_ERROR",
  HARDWARE_ERROR: "HARDWARE_ERROR",
  
  // Data errors
  VALIDATION_ERROR: "VALIDATION_ERROR",
  DATA_INTEGRITY_ERROR: "DATA_INTEGRITY_ERROR",
  
  // Configuration errors
  CONFIGURATION_ERROR: "CONFIGURATION_ERROR",
  SETTINGS_ERROR: "SETTINGS_ERROR",
  
  // UI errors
  UI_ERROR: "UI_ERROR",
  USER_INPUT_ERROR: "USER_INPUT_ERROR",
  
  // Network errors
  NETWORK_ERROR: "NETWORK_ERROR",
  API_ERROR: "API_ERROR",
} as const;

// ============================================================================
// BARREL EXPORT
// ============================================================================

const ErrorsExport = {
  // Base error class
  DystestError,
  
  // Clinical errors
  ClinicalAssessmentError,
  DyslexiaAnalysisError,
  
  // Vision & gaze errors
  VisionProcessingError,
  GazeTrackingError,
  CalibrationError,
  
  // Keystroke errors
  KeystrokeAnalysisError,
  DictionaryError,
  
  // Performance errors
  PerformanceError,
  CameraError,
  HardwareError,
  
  // Validation errors
  ValidationError,
  DataIntegrityError,
  
  // Configuration errors
  ConfigurationError,
  SettingsError,
  
  // UI errors
  UIError,
  UserInputError,
  
  // Network errors
  NetworkError,
  APIError,
  
  // Error utilities
  isOperationalError,
  isClinicalError,
  createError,
  withErrorHandling,
  compareSeverity,
  createSanitizedError,
  isUserFacingError,
  
  // Constants
  ERROR_CODES,
};

export default ErrorsExport;